import { useEffect, useMemo, useState } from "react";
import { CallSummaryDto, createCallSummaryApi } from "../api-helpers/CallSummaryApiHelper";

export type ZoomCallSummaryHookStatus =
  | "idle"
  | "loading"
  | "empty"
  | "pending"
  | "processing"
  | "completed"
  | "error";

interface UseZoomCallSummaryResult {
  status: ZoomCallSummaryHookStatus;
  summary: CallSummaryDto | null;
  errorMessage: string | null;
}

interface UseZoomCallSummaryOptions {
  /** Poll while pending/processing (default true). Set false for a one-shot fetch -
   * e.g. a historical list rendering many rows at once, where live generation isn't relevant. */
  poll?: boolean;
}

// The summary is generated by a background pipeline (Zoom webhook -> SQS -> Bedrock), not
// triggered from this UI, so this hook is read-only and polls while a summary is in
// pending/processing rather than exposing a regenerate() action.
const POLL_INTERVAL_MS = 10000;

export function useZoomCallSummary(
  appointmentId: string | undefined,
  options: UseZoomCallSummaryOptions = {}
): UseZoomCallSummaryResult {
  const { poll = true } = options;
  const api = useMemo(() => createCallSummaryApi(), []);
  const [status, setStatus] = useState<ZoomCallSummaryHookStatus>("idle");
  const [summary, setSummary] = useState<CallSummaryDto | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!appointmentId) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const applyResult = (data: CallSummaryDto | null) => {
      if (cancelled) return;
      if (!data) {
        setStatus("empty");
        return;
      }
      setSummary(data);
      if (data.status === "failed") {
        setErrorMessage(data.errorMessage);
        setStatus("error");
      } else {
        setStatus(data.status);
      }
      if (data.status !== "pending" && data.status !== "processing" && intervalId) {
        clearInterval(intervalId);
      }
    };

    const fetchSummary = async () => {
      try {
        const { data } = await api.getCallSummary(appointmentId);
        applyResult(data);
      } catch {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage("Unable to load the call summary.");
        }
        if (intervalId) clearInterval(intervalId);
      }
    };

    setStatus("loading");
    setErrorMessage(null);
    fetchSummary();
    if (poll) {
      intervalId = setInterval(fetchSummary, POLL_INTERVAL_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [api, appointmentId, poll]);

  return { status, summary, errorMessage };
}
